/**
 * CompanyScopeSelector.jsx — 사이드바 상단 테넌트/회사 선택창 (2026-09-03 개편 1차)
 *
 * 관리자 카드(SidebarHeader) 바로 아래에 표시. 권한 따라 자동 축소:
 *   운영자 = [테넌트 선택] + [회사 선택] / 비운영자 = [회사 선택]만 / 접근 회사 0 = 비노출
 *
 * 회사 칸 맨 아래 "통합관리"(2026-10-01, 사용자 확정 A안): 쓸 수 있는 통합 메뉴가 있을 때만 생긴다.
 *   회사가 아니라서 고르면 통합 관리 홈으로 가기만 한다(integratedScope.js). 통합 모드에서는 칸을 파랗게 칠한다.
 */
import React from 'react';
import { Building2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { INTEGRATED_OPTION } from './integratedScope';
import { useCompanyScope } from './CompanyScopeContext';

const selectCls = [
  'w-full h-8 rounded-lg border border-[var(--border)] bg-[var(--bg-card)]',
  'px-2 text-[12px] font-bold text-[var(--text)] outline-none cursor-pointer',
].join(' ');

// 테넌트 칸 글자 — "이름 (코드)", 이름과 코드가 같으면 한 번만 (2026-09-28 사용자 확정).
//   칸이 좁아 'SUNCREAM (SUNCREAM)' 은 끝이 잘렸다(PC 안쪽 143px 에 146px, 휴대폰 163px 에 195px).
function tenantLabel(t) {
  const code = String(t.code || '');
  const name = String(t.name || '').trim();
  return name && name.toUpperCase() !== code.toUpperCase() ? `${name} (${code})` : code;
}

export default function CompanyScopeSelector() {
  const scope = useCompanyScope();
  if (!scope || !scope.showSelector) return null;
  const {
    isOperator, tenants, selectedTenant, selectTenant, companies, company, selectCompany,
    canUseIntegrated, integrated, selectIntegrated,
  } = scope;
  const choiceCount = companies.length + (canUseIntegrated ? 1 : 0);

  return (
    <div className="mx-2.5 mb-1.5 rounded-xl border border-[var(--border)] bg-[var(--bg-subtle)] p-2 flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5 px-0.5">
        <Building2 size={12} className="text-[var(--text-muted)] flex-shrink-0" />
        <span className="text-[9px] font-black text-[var(--text-muted)] uppercase tracking-[0.08em]">작업 대상</span>
      </div>
      {isOperator && (
        <select
          aria-label="테넌트 선택"
          className={selectCls}
          value={selectedTenant?.code || ''}
          onChange={(e) => selectTenant(e.target.value)}
        >
          {tenants.length === 0 && <option value="">테넌트 없음</option>}
          {/* 테넌트 칸은 테넌트 이름·코드(tenantLabel). 예전엔 간판 이름(display_name)을 먼저 보여 줘서, 간판을
              회사 이름과 같게 둔 테넌트(SUNCREAM '유기자차')는 위 칸(테넌트)과 아래 칸(회사)이 같은 이름으로 보였다.
              간판은 그 테넌트 직원 화면에서만 쓴다. */}
          {tenants.map((t) => (
            <option key={t.code} value={t.code}>{tenantLabel(t)}</option>
          ))}
        </select>
      )}
      <select
        aria-label="회사 선택"
        className={cn(selectCls, integrated && 'border-accent-300 bg-accent-50 font-black text-accent-700 dark:bg-accent-500/10 dark:text-accent-300')}
        value={integrated ? INTEGRATED_OPTION : (company?.code || '')}
        onChange={(e) => (e.target.value === INTEGRATED_OPTION ? selectIntegrated() : selectCompany(e.target.value))}
        disabled={choiceCount <= 1}
      >
        {companies.length === 0 && <option value="">회사 없음</option>}
        {companies.map((c) => (
          <option key={c.code} value={c.code}>{c.name || c.code}</option>
        ))}
        {canUseIntegrated && <option disabled value="__sep__">──────────</option>}
        {canUseIntegrated && <option value={INTEGRATED_OPTION}>통합관리</option>}
      </select>
    </div>
  );
}
